import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, RefreshCw, Loader2, Banknote, Clock } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

function fmt(ts) {
  if (!ts) return '—';
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

const STATUS_BADGE = {
  pending: { label: 'Pending', variant: 'default', className: 'bg-amber-500/15 text-amber-500 border-amber-500/40' },
  approved: { label: 'Approved', variant: 'default', className: 'bg-green-500/15 text-green-500 border-green-500/40' },
  rejected: { label: 'Rejected', variant: 'default', className: 'bg-destructive/15 text-destructive border-destructive/40' },
};

/**
 * Admin section: list + approve/reject bank-account requests.
 * Mounted inside AdminPanel as its own tab.
 */
export default function BankAccountRequests() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null); // request id currently approving/rejecting
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/bank-account-requests`);
      setRows(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load requests');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchRows(); }, []);

  const approve = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API_URL}/api/admin/bank-account-requests/${encodeURIComponent(id)}/approve`);
      toast.success('Approved — organizer notified.');
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Approval failed');
    } finally { setBusyId(null); }
  };

  const openReject = (row) => {
    setRejectTarget(row);
    setRejectReason('');
    setRejectOpen(true);
  };

  const submitReject = async () => {
    const reason = rejectReason.trim();
    if (!reason) {
      toast.error('Rejection reason is required');
      return;
    }
    setBusyId(rejectTarget.request_id);
    try {
      await axios.post(
        `${API_URL}/api/admin/bank-account-requests/${encodeURIComponent(rejectTarget.request_id)}/reject`,
        { reason }
      );
      toast.success('Rejected — organizer notified.');
      setRejectOpen(false);
      setRejectTarget(null);
      setRejectReason('');
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rejection failed');
    } finally { setBusyId(null); }
  };

  const pendingCount = rows.filter(r => r.status === 'pending').length;

  return (
    <div data-testid="bank-requests-section">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-3 flex-wrap">
            <span className="flex items-center gap-2">
              <Banknote className="w-5 h-5" /> Bank Account Requests
              {pendingCount > 0 && (
                <Badge variant="destructive" className="ml-2" data-testid="pending-count-badge">
                  {pendingCount} pending
                </Badge>
              )}
            </span>
            <Button size="sm" variant="outline" onClick={fetchRows} disabled={loading} data-testid="bank-requests-refresh-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Refresh
            </Button>
          </CardTitle>
          <CardDescription>
            Organizer requests to add banking information. Approve to let them add details, reject with a reason to send a notification.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground text-sm">
              {loading ? 'Loading…' : 'No requests yet.'}
            </div>
          ) : (
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3">Organizer</th>
                    <th className="text-left p-3">Email</th>
                    <th className="text-left p-3">Requested</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y" data-testid="bank-requests-tbody">
                  {rows.map(r => {
                    const badge = STATUS_BADGE[r.status] || STATUS_BADGE.pending;
                    const isBusy = busyId === r.request_id;
                    return (
                      <tr key={r.request_id} data-testid={`bank-request-row-${r.request_id}`}>
                        <td className="p-3">{r.organizer_name || '—'}</td>
                        <td className="p-3 text-muted-foreground">{r.organizer_email || '—'}</td>
                        <td className="p-3 text-xs">
                          <div>{fmt(r.requested_at)}</div>
                          {r.reviewed_at && (
                            <div className="text-muted-foreground flex items-center gap-1 mt-1">
                              <Clock className="w-3 h-3" /> reviewed {fmt(r.reviewed_at)}
                            </div>
                          )}
                        </td>
                        <td className="p-3">
                          <Badge variant="outline" className={badge.className} data-testid={`bank-request-status-${r.request_id}`}>
                            {badge.label}
                          </Badge>
                          {r.status === 'rejected' && r.rejection_reason && (
                            <p className="text-xs text-muted-foreground mt-1 max-w-[200px] truncate" title={r.rejection_reason}>
                              {r.rejection_reason}
                            </p>
                          )}
                        </td>
                        <td className="p-3 text-right">
                          {r.status === 'pending' ? (
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                onClick={() => approve(r.request_id)}
                                disabled={isBusy}
                                data-testid={`bank-request-approve-btn-${r.request_id}`}
                              >
                                {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => openReject(r)}
                                disabled={isBusy}
                                data-testid={`bank-request-reject-btn-${r.request_id}`}
                              >
                                <XCircle className="w-3 h-3 mr-1" />
                                Reject
                              </Button>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject bank account request</DialogTitle>
            <DialogDescription>
              {rejectTarget && (
                <>Rejecting <strong>{rejectTarget.organizer_name || rejectTarget.organizer_email}</strong>. The organizer will receive an email with the reason below.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="reject-reason-input">Reason (required)</label>
            <Textarea
              id="reject-reason-input"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Could not verify account ownership documents"
              rows={4}
              data-testid="reject-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectOpen(false)} disabled={busyId === rejectTarget?.request_id}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={submitReject}
              disabled={!rejectReason.trim() || busyId === rejectTarget?.request_id}
              data-testid="reject-confirm-btn"
            >
              {busyId === rejectTarget?.request_id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <XCircle className="w-4 h-4 mr-1" />}
              Reject and notify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
